import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { SidebarFrameItem } from "./sidebar-frame-item";
import { SidebarTreeItem } from "./sidebar-tree-item";
import { SidebarTaskItem } from "./sidebar-task-item";
import { ExecutionSidebar } from "./execution-sidebar";
import type { ExecutionSidebarProps } from "./execution-sidebar";

/**
 * Workshop: Left sidebar panel — 240px wide, full-height scrollable panel.
 */

type SidebarTab = "frames" | "browse";

interface FrameEntry {
  id: string;
  title: string;
  icon: LucideIcon;
  active?: boolean;
  statusColor?: string;
}

interface TreeEntry {
  id: string;
  label: string;
  icon: LucideIcon;
  expanded?: boolean;
  depth?: number;
}

interface TaskEntry {
  id: string;
  title: string;
  checked?: boolean;
  agentLetter?: string;
  agentColor?: string;
}

interface LeftSidebarProps {
  activeTab?: SidebarTab;
  frames?: FrameEntry[];
  treeItems?: TreeEntry[];
  tasks?: TaskEntry[];
  onTabChange?: (tab: SidebarTab) => void;
  onAddFrame?: () => void;
  onSelectFrame?: (frameId: string) => void;
  onSelectTreeItem?: (itemId: string) => void;
  onToggleTreeItem?: (itemId: string) => void;
  onSelectTask?: (taskId: string) => void;
  onToggleTask?: (taskId: string) => void;
  onAddTask?: () => void;
  /** When set, replaces the Tasks section with the execution progress view. */
  execution?: ExecutionSidebarProps;
  className?: string;
  "data-testid"?: string;
}

function SidebarTabButton({
  label,
  icon,
  isActive,
  onClick,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-semibold transition-colors",
        isActive
          ? "bg-glass-fill-heavy text-primary"
          : "text-muted hover:bg-glass-stroke-subtle hover:text-secondary",
      )}
      onClick={onClick}
      data-testid={testId}
      aria-selected={isActive}
      role="tab"
    >
      {icon}
      {label}
    </button>
  );
}

export function LeftSidebar({
  activeTab = "frames",
  frames = [],
  treeItems = [],
  tasks = [],
  onTabChange,
  onAddFrame,
  onSelectFrame,
  onSelectTreeItem,
  onToggleTreeItem,
  onSelectTask,
  onToggleTask,
  onAddTask,
  execution,
  className,
  "data-testid": testId = "left-sidebar",
}: LeftSidebarProps) {
  return (
    <aside
      className={cn(
        "flex w-60 flex-col rounded-2xl border border-glass-stroke bg-glass-fill shadow-glass-sm backdrop-blur-glass-sm",
        className,
      )}
      data-testid={testId}
    >
      {/* Tab switcher */}
      <div
        className="flex items-center gap-1 border-b border-glass-stroke-subtle px-2 py-2"
        role="tablist"
        aria-label="Sidebar tabs"
      >
        <SidebarTabButton
          label="Frames"
          icon={<Icons.Layers className="h-3.5 w-3.5" />}
          isActive={activeTab === "frames"}
          onClick={() => onTabChange?.("frames")}
          testId="sidebar-tab-frames"
        />
        <SidebarTabButton
          label="Browse"
          icon={<Icons.FolderTree className="h-3.5 w-3.5" />}
          isActive={activeTab === "browse"}
          onClick={() => onTabChange?.("browse")}
          testId="sidebar-tab-browse"
        />
      </div>

      {/* Scrollable content */}
      <div className="scrollbar-none flex-1 overflow-y-auto">
        {activeTab === "frames" && (
          <div className="flex flex-col" data-testid="sidebar-frame-list">
            <div className="flex items-center justify-between px-3 py-1.5">
              <span className="text-[9px] font-medium uppercase tracking-wider text-muted">
                Frames
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onAddFrame}
                data-testid="sidebar-add-frame"
                aria-label="Add frame"
              >
                <Icons.Plus className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex flex-col">
              {frames.map((frame) => (
                <SidebarFrameItem
                  key={frame.id}
                  title={frame.title}
                  icon={frame.icon}
                  active={frame.active}
                  statusColor={frame.statusColor}
                  onClick={() => onSelectFrame?.(frame.id)}
                  data-testid={`sidebar-frame-${frame.id}`}
                />
              ))}
              {frames.length === 0 && (
                <p className="px-4 py-3 text-center text-[10px] text-muted">
                  No frames yet
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === "browse" && (
          <div className="flex flex-col" data-testid="sidebar-browse-tree">
            <div className="flex items-center px-3 py-1.5">
              <span className="text-[9px] font-medium uppercase tracking-wider text-muted">
                Projects
              </span>
            </div>
            <div className="flex flex-col">
              {treeItems.map((item) => (
                <SidebarTreeItem
                  key={item.id}
                  label={item.label}
                  icon={item.icon}
                  expanded={item.expanded}
                  depth={item.depth}
                  onToggle={() => onToggleTreeItem?.(item.id)}
                  onClick={() => onSelectTreeItem?.(item.id)}
                  data-testid={`sidebar-tree-${item.id}`}
                />
              ))}
              {treeItems.length === 0 && (
                <p className="px-4 py-3 text-center text-[10px] text-muted">
                  No projects yet
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom section — execution progress or task list */}
      <div
        className="shrink-0 border-t border-glass-stroke-subtle"
        data-testid="sidebar-bottom-section"
      >
        {execution ? (
          <div className="max-h-64 overflow-hidden" data-testid="sidebar-execution-section">
            <ExecutionSidebar {...execution} />
          </div>
        ) : (
          <div data-testid="sidebar-tasks-section">
            <div className="flex items-center gap-1.5 px-4 py-1.5">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span className="text-[11px] font-semibold text-secondary">Tasks</span>
              <span className="flex-1" />
              {tasks.length > 0 && (
                <span className="text-[10px] font-medium text-muted">{tasks.length}</span>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onAddTask}
                data-testid="sidebar-add-task"
                aria-label="Add task"
              >
                <Icons.Plus className="h-3 w-3" />
              </Button>
            </div>
            <div className="scrollbar-none flex max-h-40 flex-col overflow-y-auto">
              {tasks.map((task) => (
                <SidebarTaskItem
                  key={task.id}
                  title={task.title}
                  checked={task.checked}
                  agentLetter={task.agentLetter}
                  agentColor={task.agentColor}
                  onToggle={() => onToggleTask?.(task.id)}
                  onClick={() => onSelectTask?.(task.id)}
                  data-testid={`sidebar-task-${task.id}`}
                />
              ))}
              {tasks.length === 0 && (
                <p className="px-4 py-2 text-center text-[10px] text-muted">
                  No tasks yet
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

export type { LeftSidebarProps, SidebarTab, FrameEntry, TreeEntry, TaskEntry };
